import React, { useState } from "react";
import { useSelector } from "react-redux";
import moment from "moment";
import { EuiFlexGroup, EuiFlexItem, EuiText, EuiIcon, EuiButton } from "@elastic/eui";
import _ from "lodash";
import { useHistory } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";
import * as S from "../../style";
import { extractResourceAttributes } from "../../../../utils/jsonApiUtils";
import { pointsSelector, ordersSelector } from "../../slice";
import DropOffDetailsOverlay from "../shared/DropOffDetailsOverlay";
import useWindowDimensions from "../../../../hooks/useWindowDimensions";
import TripBadge from "../shared/TripBadge";
import { BREAKPOINT_TABLET } from "../../../../constants/style/breakpoints";
import { getCurrentPointId, sortPointsByPosition } from "../../utils";
import tripStatus from "../../constants/tripStates";
import TripActionModal from "../shared/TripActionModal";
import TripActions from "./TripActions";
import TripTags from "./TripTags";
import {
    dateToFormattedFullDateAndTime,
    secondsToHoursAndMinutes,
} from "../../../../utils/dateAndTime";
import DatePickerInput from "../../../../components/datePicker";
import ModalComponent from "../../../../components/modal";

const showTripDetailsStep = (clickHandler) => ({
    title: "...",
    step: "...",
    isComplete: false,
    isSelected: false,
    color: "#fff",
    onClick: clickHandler,
});

const isTripPointCompleted = (tripPoints, currentTripPointId) => {
    let isCurrentTripFound = false;

    tripPoints.forEach((point) => {
        if (+point.id === +currentTripPointId) {
            isCurrentTripFound = true;
        }
    });

    return isCurrentTripFound;
};

const generateSteps = (tripPoints, currentTripPointId, tripStatues) => {
    const currentPointId = getCurrentPointId(tripPoints, currentTripPointId, tripStatues);
    let isCurrentTripFound = false;

    return tripPoints.map((point) => {
        const title = point.locationAttributes
            ? point.locationAttributes.data.attributes.nameAr ||
              point.locationAttributes.data.attributes.nameEn ||
              point.locationAttributes.data.attributes.name ||
              `${point.locationAttributes.data.attributes.address} (${point.locationAttributes.data.id})`
            : point.cityName;

        const step = {
            title,
            // isComplete: !!(currentPointId && !isCurrentTripFound && tripStatues !== "pending"),
            isComplete: point.visited,
            isSelected: false,
            color: "#fff",
            onClick: () => {},
        };

        if (+point.id === currentPointId) {
            isCurrentTripFound = true;
        }

        return step;
    });
};

const isStatusEditable = (status) =>
    status !== tripStatus.COMPLETED &&
    status !== tripStatus.CANCELLED &&
    status !== tripStatus.NOT_REQUESTED &&
    status !== tripStatus.UNFULFILLED &&
    status !== tripStatus.NOT_LOADED &&
    status !== tripStatus.ABSENT;

export default function TripHeaderView({
    isCurrentUserSuperAdmin,
    tripStatues,
    tripTags,
    tripId,
    tripCancellationReason,
    tripCancelledBy,
    supplierName,
    driverName,
    truckPlateNumber,
    dropoffs,
    manualDistance,
    pickupArrivalTime,
    pickupDepartureTime,
    lastDropoffDepartureTime,
    tripDuration,
    currentTripPointId,
    branchNameAr,
    branchNameEn,
    truckTypeAlias,
    contractType,
    onTripStatusChange,
    onSelectTag,
    onDuplicateAction,
    isLoading,
    handleOpenRouteEnhancementModal,
}) {
    const intl = useIntl();
    const order = extractResourceAttributes(useSelector(ordersSelector()));
    const tripPoints = useSelector(pointsSelector());

    const tripPickup = tripPoints.find((point) => point.pointType === "pickup") || {};
    const tripDropOffs = sortPointsByPosition(
        tripPoints.filter((point) => point.pointType === "dropoff")
    );
    const locationAttributes = extractResourceAttributes(tripPickup.locationAttributes);

    const [showPointsDetails, setShowPointsDetails] = useState(false);
    const [duplicateDate, setDuplicateDate] = useState(null);
    const [duplicateDateError, setDuplicateDateError] = useState(false);
    const { windowWidth } = useWindowDimensions();
    const hidePointDetails = tripPoints.length > 6 || windowWidth <= BREAKPOINT_TABLET;

    const [isSavingStatus, setSavingStatus] = useState(false);
    const [isTripStatusConfirmationModalVisible, setIsTripStatusConfirmationModalVisible] =
        useState(false);
    const [isTripDuplicateConfirmationModalVisible, setIsTripDuplicateConfirmationModalVisible] =
        useState(false);
    const [activeTripAction, setActiveTripAction] = useState();
    const history = useHistory();

    const hidePiontsDetails = () => {
        setShowPointsDetails(true);
    };

    const showTripStatusConfirmationModal = (actionValue) => {
        setIsTripStatusConfirmationModalVisible(true);
        setActiveTripAction(actionValue);
    };

    const hideTripStatusConfirmationModal = () => {
        setIsTripStatusConfirmationModalVisible(false);
        setActiveTripAction(null);
    };

    const onConfirmTripStatus = (cancellationReason) => {
        handleTripStatus(activeTripAction, cancellationReason);
        hideTripStatusConfirmationModal();
    };

    const handleTripStatus = (value, reason) => {
        setSavingStatus(true);
        onTripStatusChange({ value, meta: reason }, () => setSavingStatus(false));
    };

    const handleDuplicateAction = () => {
        if (duplicateDate === null) {
            setDuplicateDateError(true);
        } else {
            onDuplicateAction((duplicatedTripId) => {
                setIsTripDuplicateConfirmationModalVisible(false);
                duplicatedTripId && history.push(`/database/trips/${duplicatedTripId}`);
            }, duplicateDate);
        }
    };

    const handleTagsChange = (selectedTags) => onSelectTag({ tagList: selectedTags });

    const handleRemoveTag = (tag) => {
        const selectedTagsClone = [...tripTags];
        const index = tripTags.indexOf(tag);

        selectedTagsClone.splice(index, 1);
        handleTagsChange(selectedTagsClone);
    };

    const completeActionPreviewText = () => {
        const supplierInfo = `${intl.formatMessage({
            id: "trip.supplier",
        })}: ${supplierName || "N/A"}`;
        const driverInfo = `${intl.formatMessage({
            id: "trip.driver",
        })}: ${driverName || "N/A"}`;
        const truckInfo = `${intl.formatMessage({
            id: "trip.truck",
        })}: ${truckPlateNumber || "N/A"}`;
        const dropoffsInfo = `${intl.formatMessage({
            id: "trip.dropoffs",
        })}: ${dropoffs.join(" ,") || "N/A"}`;
        const manualDistanceInfo = `${intl.formatMessage({
            id: "trip.manual-distance",
        })}: ${manualDistance ? `${manualDistance} KM` : "N/A"}`;
        const pickupArrivalTimeInfo = `${intl.formatMessage({
            id: "trip.pickup-arrival-time",
        })}: ${(pickupArrivalTime && dateToFormattedFullDateAndTime(pickupArrivalTime)) || "N/A"}`;
        const pickupDepartureTimeInfo = `${intl.formatMessage({
            id: "trip.pickup-departure-time",
        })}: ${
            (pickupDepartureTime && dateToFormattedFullDateAndTime(pickupDepartureTime)) || "N/A"
        }`;
        const departureTimeInfo = `${intl.formatMessage({
            id: "trip.dropoff-departure-time",
        })}: ${
            (lastDropoffDepartureTime &&
                dateToFormattedFullDateAndTime(lastDropoffDepartureTime)) ||
            "N/A"
        }`;
        const tripDurationInfo = `${intl.formatMessage({
            id: "trip.duration",
        })}: ${secondsToHoursAndMinutes(tripDuration) || "N/A"}`;
        const branchInfo = `${intl.formatMessage({
            id: "trip.branch",
        })}: ${branchNameAr} / ${branchNameEn}`;
        const truckTypeAliasInfo = `${intl.formatMessage({
            id: "trip.truck-type",
        })}: ${truckTypeAlias || "N/A"}`;

        const contractTypeInfo = `${intl.formatMessage({
            id: "trip.contract-type",
        })}: ${
            contractType
                ? intl.formatMessage({ id: `contractType.${_.kebabCase(contractType)}` })
                : "N/A"
        }`;

        return `${branchInfo}
        ${truckTypeAliasInfo}
        ${supplierInfo}
        ${driverInfo}
        ${truckInfo}
        ${dropoffsInfo}
        ${manualDistanceInfo}
        ${pickupArrivalTimeInfo}
        ${pickupDepartureTimeInfo}
        ${departureTimeInfo}
        ${tripDurationInfo}
        ${contractTypeInfo}`;
    };

    let steps = generateSteps(tripPoints, currentTripPointId, tripStatues);

    tripPoints.length >= 3 && hidePointDetails
        ? (steps[1] = {
              ...showTripDetailsStep(hidePiontsDetails),
              isComplete: isTripPointCompleted(tripPoints, currentTripPointId),
          })
        : (steps = generateSteps(tripPoints, currentTripPointId, tripStatues));

    return (
        <S.TripHeader
            gutterSize="none"
            justifyContent="flexStart"
            responsive={false}
        >
            {isTripStatusConfirmationModalVisible && activeTripAction && (
                <TripActionModal
                    action={activeTripAction}
                    tripCancellationReason={tripCancellationReason}
                    tripCancelledBy={tripCancelledBy}
                    completionActionPreview={completeActionPreviewText()}
                    onConfirm={(tripAction) => onConfirmTripStatus(tripAction)}
                    onCancel={hideTripStatusConfirmationModal}
                />
            )}
            <ModalComponent
                open={isTripDuplicateConfirmationModalVisible}
                header={
                    <h1>
                        <FormattedMessage id="trip-modal.duplicate-trip-header" />
                    </h1>
                }
                isFooter
                cancelBtn
                submitBtn
                disabled={isLoading}
                submitBtnText={<FormattedMessage id="trip-modal.duplicate-submit" />}
                onSubmit={handleDuplicateAction}
                onClose={() => setIsTripDuplicateConfirmationModalVisible(false)}
                borderedHeader
            >
                <EuiText size="s">
                    <FormattedMessage id="trip-modal.duplicate-trip-content" />
                </EuiText>
                <DatePickerInput
                    isEditable
                    name="datePicker"
                    selectedValue={duplicateDate}
                    onChange={(e) => {
                        setDuplicateDate(e);
                        setDuplicateDateError(false);
                    }}
                    required="true"
                    label={<FormattedMessage id="trip.pickup-time" />}
                    error={duplicateDateError}
                    dateFormat="dd/MM/yyyy h:mma"
                    placeHolder="Choose date & time"
                />
            </ModalComponent>
            <EuiFlexItem
                grow={false}
                className="trip-header__icon-container"
            />

            <EuiFlexGroup
                direction="column"
                gutterSize="none"
            >
                <EuiFlexGroup
                    gutterSize="none"
                    alignItems="center"
                    justifyContent="spaceBetween"
                    direction="row"
                    className="trip-header-content"
                    responsive
                >
                    <EuiFlexItem
                        grow={false}
                        className="trip-header__title-container"
                    >
                        <h2 className="trip-header__title">
                            <EuiIcon
                                type="branch"
                                size="l"
                            />
                            <FormattedMessage
                                id="trip.number"
                                values={{ value: tripId }}
                            />
                        </h2>
                        <TripBadge status={tripStatues} />
                    </EuiFlexItem>

                    {tripDropOffs.length === 0 && (
                        <EuiFlexGroup
                            style={{ marginLeft: 12, color: "white" }}
                            gutterSize="none"
                            direction="column"
                        >
                            <EuiFlexItem>
                                <EuiText size="s">
                                    <FormattedMessage id="trip-header.pickup-from" />
                                </EuiText>
                            </EuiFlexItem>

                            <EuiFlexItem>
                                <b>
                                    {order.organizationName ? `${order.organizationName}` : ""}
                                    {locationAttributes && locationAttributes.name
                                        ? ` - ${locationAttributes.name}`
                                        : ""}
                                    {tripPickup.arrivalTime
                                        ? ` at ${moment(tripPickup.arrivalTime).format("hh:mm A")}`
                                        : ""}
                                </b>
                            </EuiFlexItem>
                        </EuiFlexGroup>
                    )}

                    {tripTags?.length > 0 && (
                        <TripTags
                            tags={tripTags}
                            onRemove={(tag) => handleRemoveTag(tag)}
                        />
                    )}

                    <div className="trip-header__actions-icon-wrapper">
                        <EuiFlexGroup alignItems="center">
                            <EuiFlexItem>
                                <EuiButton
                                    className="trip-header_duplicate-button"
                                    iconType="copy"
                                    size="m"
                                    onClick={() => {
                                        setIsTripDuplicateConfirmationModalVisible(true);
                                    }}
                                >
                                    <FormattedMessage id="trip.duplicate-action" />
                                </EuiButton>
                            </EuiFlexItem>
                            <EuiFlexItem id="trip__actions--dottes">
                                <TripActions
                                    isSaving={isSavingStatus}
                                    onConfirm={(actionValue) => {
                                        if (actionValue === "enhanceRoute") {
                                            handleOpenRouteEnhancementModal();
                                        } else {
                                            showTripStatusConfirmationModal(actionValue);
                                        }
                                    }}
                                    selectedTags={tripTags}
                                    onSelectTag={(selectedTags) => handleTagsChange(selectedTags)}
                                    isStatusEditable={
                                        isStatusEditable(tripStatues) || isCurrentUserSuperAdmin
                                    }
                                />{" "}
                            </EuiFlexItem>
                        </EuiFlexGroup>
                    </div>
                </EuiFlexGroup>

                {tripDropOffs.length > 0 && (
                    <EuiFlexGroup>
                        <EuiFlexItem>
                            <S.TripSectioDescriptionWrapper>
                                <S.TripHeaderSteps
                                    size="s"
                                    hidePoints={hidePointDetails}
                                    steps={steps}
                                />
                            </S.TripSectioDescriptionWrapper>
                            {showPointsDetails && (
                                <DropOffDetailsOverlay
                                    onHide={() => setShowPointsDetails(false)}
                                    tripPoints={tripPoints}
                                    currentTripPointId={currentTripPointId}
                                    tripStatus={tripStatues}
                                />
                            )}
                        </EuiFlexItem>
                    </EuiFlexGroup>
                )}
            </EuiFlexGroup>
        </S.TripHeader>
    );
}
